import { linkPrincipalPrefix, SystemTags } from '@tunasong/models'
import type { ACL, EntityType } from '@tunasong/schemas'

/** A child row from `entities/?parentIds=…&fields=id,type,parentId,updatedAt,tags` */
export interface SpaceChild {
  type: EntityType
  updatedAt: string
  parentId?: string
  tags?: string[]
  trash?: boolean
}

export interface SpaceSummary {
  /** Counted children by type */
  typeCounts: Partial<Record<EntityType, number>>
  /** All counted children */
  total: number
  /** The newest child's `updatedAt`, uncounted children included: a comment is activity */
  lastActivityAt: string
}

/** What the band Library does not list as material (plugins/bandspace/src/views/default.tsx), so not counted */
const uncountedTypes = new Set<string>(['calendar', 'calendarevent', 'comment', 'note'])

const isCounted = (child: SpaceChild) =>
  !uncountedTypes.has(child.type) && !(child.tags ?? []).includes(SystemTags.HeaderImage)

/** Group the batch response by parent: counts of what a member would call the space's material, and last activity */
export const summarizeSpaceChildren = (children: SpaceChild[]): Map<string, SpaceSummary> => {
  const summaries = new Map<string, SpaceSummary>()
  for (const child of children) {
    if (!child.parentId || child.trash) {
      continue
    }
    const summary = summaries.get(child.parentId) ?? { typeCounts: {}, total: 0, lastActivityAt: child.updatedAt }
    if (child.updatedAt > summary.lastActivityAt) {
      summary.lastActivityAt = child.updatedAt
    }
    if (isCounted(child)) {
      summary.typeCounts[child.type] = (summary.typeCounts[child.type] ?? 0) + 1
      summary.total += 1
    }
    summaries.set(child.parentId, summary)
  }
  return summaries
}

/** Nouns for a count, in tie-break order. Plurals cannot be derived from type names (`bass`), so they are listed. */
const nouns: [EntityType, string, string][] = [
  ['song', 'song', 'songs'],
  ['setlist', 'setlist', 'setlists'],
  ['page', 'page', 'pages'],
  ['folder', 'folder', 'folders'],
  ['rider', 'rider', 'riders'],
  ['audio', 'recording', 'recordings'],
  ['audioclip', 'clip', 'clips'],
  ['image', 'image', 'images'],
  ['video', 'video', 'videos'],
  ['pdf', 'PDF', 'PDFs'],
  ['poll', 'poll', 'polls'],
  ['todo', 'to-do', 'to-dos'],
]
const item: [string, string] = ['item', 'items']

const countPhrase = (count: number, [one, many]: [string, string]) => `${count} ${count === 1 ? one : many}`

/** How many counts a band's line shows, so it stays one line (Hick) */
const maxBandCounts = 2

/**
 * The row's meta line, as parts to join with `metaSeparator`: the kind, then counts. A band names its two largest
 * kinds of material ("14 songs", "3 setlists"); a project gives a total ("9 items"). Without counts, or with
 * nothing to count, only the kind: never "0".
 */
export const spaceMetaParts = (spaceType: EntityType, summary?: SpaceSummary): string[] => {
  const kind = spaceType === 'bandspace' ? 'Band' : 'Project'
  if (!summary || summary.total === 0) {
    return [kind]
  }
  if (spaceType !== 'bandspace') {
    return [kind, countPhrase(summary.total, item)]
  }
  const named = nouns
    .map(([type, one, many], order) => ({
      count: summary.typeCounts[type] ?? 0,
      noun: [one, many] as [string, string],
      order,
    }))
    .filter(entry => entry.count > 0)
  const unnamed = summary.total - named.reduce((sum, entry) => sum + entry.count, 0)
  const entries = unnamed > 0 ? [...named, { count: unnamed, noun: item, order: nouns.length }] : named
  const top = entries.sort((a, b) => b.count - a.count || a.order - b.order).slice(0, maxBandCounts)
  return [kind, ...top.map(entry => countPhrase(entry.count, entry.noun))]
}

const guestPrefix = 'guest:'

export function isGuestPrincipal(principal: string): boolean {
  return principal.startsWith(guestPrefix)
}

export const guestEmail = (principal: string) => principal.slice(guestPrefix.length)

/** Principals that are audiences, not people */
const audiencePrincipals = new Set(['PUBLIC', 'AUTHENTICATED'])

/** Mirrors `isLive` in @tunasong/models acl-access: an unparseable expiry counts as expired */
const isLiveGrant = (acl: ACL, now: number) => {
  if (acl.expiresAt === undefined) {
    return true
  }
  const expiresAt = Date.parse(acl.expiresAt)
  return !Number.isNaN(expiresAt) && expiresAt > now
}

/** The people in a space: its owner, then everyone it is shared with by name or as a guest, each once */
export const getSpaceMembers = (space: { userId: string; acls?: ACL[] }, now: number = Date.now()): string[] => {
  const people = (space.acls ?? [])
    .filter(acl => !audiencePrincipals.has(acl.principal) && !acl.principal.startsWith(linkPrincipalPrefix))
    .filter(acl => isLiveGrant(acl, now))
    .map(acl => acl.principal)
  return [...new Set([space.userId, ...people])]
}

/** A space's last activity: its own update or its newest child's, whichever is later */
export const spaceActivityAt = (space: { updatedAt: string }, summary?: SpaceSummary) =>
  summary && summary.lastActivityAt > space.updatedAt ? summary.lastActivityAt : space.updatedAt

/**
 * Ids per `entities/?parentIds=` request. Every id rides in the URL (~37 chars each), and CloudFront and API Gateway
 * cap URLs near 8 KB, so one request for 200+ spaces would fail. 50 keeps a request near 2 KB.
 */
export const metaChunkSize = 50

/** Split ids into request-sized chunks, preserving order */
export const chunkIds = (ids: string[], size: number = metaChunkSize): string[][] =>
  Array.from({ length: Math.ceil(ids.length / size) }, (_, index) => ids.slice(index * size, (index + 1) * size))
